import { z } from 'zod';
/**
 * Planos "Para seu negócio" (estudo aprovado em 23/10/2026): catálogo próprio, separado dos
 * planos pessoais. Preço por negócio, não por pessoa. O perfil no catálogo é grátis para
 * todos; o plano libera o atendimento automático e as ferramentas de gestão.
 */
export const BUSINESS_PLANS = ['autonomo', 'profissional', 'equipe', 'empresa'];
export const businessPlanSchema = z.enum(BUSINESS_PLANS);
/** O que cada plano libera (o resto do Meu negócio é o perfil, que é de todos). */
export const BUSINESS_FEATURES = [
    'atendimento',
    'clientes',
    'avisos',
    'financeiro',
    'loja',
    'clinica',
    'papeis',
    'integracoes',
];
const BASE = ['atendimento', 'clientes'];
const PRO = [...BASE, 'avisos', 'financeiro', 'loja', 'clinica'];
export const BUSINESS_PLAN_CATALOG = {
    autonomo: {
        label: 'Autônomo',
        forWhom: 'MEI e quem atende sozinho',
        priceCents: 7990,
        annualCents: 79900,
        fromPrice: false,
        seats: 1,
        responses: 600,
        voiceMinutes: 20,
        features: BASE,
        highlights: [
            'Resposta automática com a sua aprovação',
            'Agenda, página e lembretes',
            'Clientes e orçamentos',
            '600 respostas automáticas por mês',
        ],
    },
    profissional: {
        label: 'Profissional',
        forWhom: 'Profissional liberal, loja, salão e consultório',
        priceCents: 14990,
        annualCents: 149900,
        fromPrice: false,
        seats: 3,
        responses: 1200,
        voiceMinutes: 45,
        features: PRO,
        highlights: [
            'Tudo do Autônomo, para até 3 pessoas',
            'Envios em lote, avisos programados e retomadas',
            'Vitrine, pedidos e o Pix da sua loja',
            'Módulo clínica para quem é da saúde',
            '1.200 respostas automáticas por mês',
        ],
    },
    equipe: {
        label: 'Equipe',
        forWhom: 'Até 5 pessoas e várias agendas',
        priceCents: 39990,
        annualCents: 399900,
        fromPrice: false,
        seats: 5,
        responses: 3300,
        voiceMinutes: 120,
        features: [...PRO, 'papeis'],
        highlights: [
            'Tudo do Profissional, para até 5 pessoas',
            'Na clínica: papéis e permissões por pessoa',
            'Na clínica: funil de leads com responsável e prazo',
            '3.300 respostas automáticas por mês',
        ],
    },
    empresa: {
        label: 'Empresa',
        forWhom: 'Até 15 pessoas; acima disso, sob proposta',
        priceCents: 89990,
        annualCents: null,
        fromPrice: true,
        seats: 15,
        responses: 7500,
        voiceMinutes: 300,
        features: [...PRO, 'papeis', 'integracoes'],
        highlights: [
            'Unidades e volume contratado',
            'Importação assistida pela nossa equipe',
            'Atendimento direto com a nossa equipe',
            '7.500 respostas automáticas por mês',
        ],
    },
};
/** O que o perfil grátis tem, para a tela comparar sem esconder. */
export const BUSINESS_FREE_HIGHLIGHTS = [
    'Perfil no catálogo da cidade',
    'Recebe pedidos e orçamentos',
    'Responde à mão, pelo app ou pela web',
    'Lembrete com Confirmar e Remarcar a qualquer cliente',
    'Assume a sua página da vitrine do bairro',
];
/** Avulsos, os dois com lucro no pior caso. */
export const BUSINESS_EXTRA_SEAT_CENTS = 6990;
export const BUSINESS_RESPONSE_PACK = { responses: 1000, priceCents: 7990 };
/** Teste de 14 dias sem cartão, com teto; um por telefone e por CNPJ. Vale como o Profissional. */
export const BUSINESS_TRIAL = {
    days: 14,
    responses: 150,
    voiceMinutes: 10,
    plan: 'profissional',
};
export const BUSINESS_PLAN_SOURCES = ['teste', 'assinatura', 'cortesia', 'legado'];
const meter = z.object({
    used: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
});
export const businessPlanStatusSchema = z.object({
    tier: z.union([businessPlanSchema, z.literal('gratis')]),
    /** A conta já tem um negócio (perfil no Meu negócio ou clínica própria). */
    hasBusiness: z.boolean(),
    source: z.enum(BUSINESS_PLAN_SOURCES).nullable(),
    /** Fim do teste, quando é teste. */
    trialEndsAt: z.string().nullable(),
    /** O teste ainda pode começar para esta conta (telefone sem teste antes). */
    trialAvailable: z.boolean(),
    /** Até quando vale (assinatura e cortesia com prazo). */
    validUntil: z.string().nullable(),
    responses: meter,
    voiceMinutes: meter,
    seats: meter,
    features: z.array(z.enum(BUSINESS_FEATURES)),
    /** A assinatura pela web já está ligada (preços no meio de pagamento). */
    checkout: z.boolean(),
    /** Mensal ou anual, quando é assinatura. */
    interval: z.enum(['month', 'year']).nullable().default(null),
    /** Pessoas a mais compradas além das do plano. */
    extraSeats: z.number().int().nonnegative().default(0),
    /** A assinatura foi cancelada e vale até `validUntil`. */
    cancelAtPeriodEnd: z.boolean().default(false),
});
/** O Empresa é sob proposta: a assinatura pela web vai até o Equipe. */
export const BUSINESS_WEB_PLANS = ['autonomo', 'profissional', 'equipe'];
export const businessCheckoutSchema = z.object({
    plan: z.enum(BUSINESS_WEB_PLANS),
    interval: z.enum(['month', 'year']).default('month'),
});
export const businessSeatsSchema = z.object({ extra: z.number().int().min(0).max(50) });
/** Volta do pagamento: a página confere a sessão sem esperar o aviso do Stripe. */
export const businessConfirmSchema = z.object({ session: z.string().trim().min(8).max(200) });
export const businessCheckoutResultSchema = z.object({
    url: z.string().nullable(),
    changed: z.boolean(),
});
export function businessHas(status, feature) {
    return status.features.includes(feature);
}
/** O plano mínimo que traz um recurso (para a tela dizer "faz parte do Profissional"). */
export function businessPlanFor(feature) {
    return (BUSINESS_PLANS.find((plan) => BUSINESS_PLAN_CATALOG[plan].features.includes(feature)) ??
        'empresa');
}
